import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import html2canvas from "html2canvas";
import { MessageSquareText, X, AlertTriangle, Lightbulb, Loader2, Check, Camera } from "lucide-react";
import { submitInContextFeedback } from "@/lib/api";

// Rolling, in-context feedback. A small unobtrusive floating button opens a side
// drawer where the learner reports a problem or shares a suggestion. Rich context
// (stage, coaching target, recent messages, draft) + a screenshot of the screen at
// the moment they clicked are captured automatically. Never auto-opens.
export default function InContextFeedback({ sessionId, getContext }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("problem"); // "problem" | "suggestion"
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [shot, setShot] = useState(null);       // captured screenshot data URL
  const [capturing, setCapturing] = useState(false);

  const captureScreenshot = useCallback(async () => {
    setCapturing(true);
    try {
      const canvas = await html2canvas(document.body, {
        logging: false,
        useCORS: true,
        scale: Math.min(window.devicePixelRatio || 1, 1),
        backgroundColor: "#faf9f6",
        ignoreElements: (el) => el.getAttribute?.("data-testid") === "incontext-feedback-drawer"
          || el.getAttribute?.("data-testid") === "incontext-feedback-button",
        windowWidth: document.documentElement.clientWidth,
        windowHeight: document.documentElement.clientHeight,
      });
      return canvas.toDataURL("image/jpeg", 0.55);
    } catch (e) {
      return null; // screenshot is best-effort; feedback still submits without it
    } finally {
      setCapturing(false);
    }
  }, []);

  const openDrawer = useCallback(async () => {
    // Capture what the learner is looking at BEFORE the drawer covers it.
    const img = await captureScreenshot();
    setShot(img);
    setType("problem");
    setComment("");
    setDone(false);
    setOpen(true);
  }, [captureScreenshot]);

  const close = () => { setOpen(false); setShot(null); };

  const submit = async () => {
    if (saving || !comment.trim()) return;
    setSaving(true);
    let context = {};
    try { context = getContext ? getContext() : {}; } catch (e) { context = {}; }
    context.url = window.location.href;
    try {
      await submitInContextFeedback(sessionId, {
        feedback_type: type,
        comment: comment.trim(),
        context,
        screenshot: shot || null,
      });
    } catch (e) {
      // never trap the learner — acknowledge regardless
    }
    setSaving(false);
    setDone(true);
    setTimeout(() => { close(); }, 1600);
  };

  const typeBtn = (val, label, Icon, testId) => (
    <button
      type="button"
      data-testid={testId}
      onClick={() => setType(val)}
      className={`flex-1 flex items-center justify-center gap-1.5 text-[12.5px] py-2 rounded-sm border transition-colors ${
        type === val
          ? val === "problem"
            ? "border-amber-400 bg-amber-50 text-amber-800"
            : "border-[#8C3A2A] bg-[#8C3A2A]/[0.06] text-[#8C3A2A]"
          : "border-stone-200 text-stone-500 hover:border-stone-400"
      }`}
    >
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );

  return (
    <>
      {/* Floating trigger — unobtrusive, never auto-opens */}
      {!open && (
        <button
          onClick={openDrawer}
          disabled={capturing}
          data-testid="incontext-feedback-button"
          className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 bg-white/90 backdrop-blur border border-stone-300 text-stone-600 px-3.5 py-2 rounded-full shadow-sm hover:border-[#8C3A2A] hover:text-[#8C3A2A] hover:-translate-y-px transition-[transform,color,border-color] text-[12.5px] font-medium disabled:opacity-60"
        >
          {capturing ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquareText className="h-4 w-4" />}
          Thoughts?
        </button>
      )}

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-stone-900/20"
              onClick={close}
            />
            <motion.aside
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              data-testid="incontext-feedback-drawer"
              className="fixed top-0 right-0 z-50 h-full w-full max-w-md bg-[#fdfcfa] border-l border-stone-200 shadow-2xl flex flex-col"
            >
              <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-stone-200">
                <div>
                  <h2 className="font-serif-display text-xl text-stone-900">Tell us what you're thinking.</h2>
                  <p className="text-[13px] text-stone-500 mt-1 leading-relaxed">
                    Something confusing? Something helpful? Something frustrating? Have an idea? We'd like to hear it.
                  </p>
                </div>
                <button onClick={close} data-testid="incontext-feedback-close" className="shrink-0 text-stone-400 hover:text-stone-700 transition-colors">
                  <X className="h-5 w-5" />
                </button>
              </div>

              {done ? (
                <div className="flex-1 flex flex-col items-center justify-center px-6 text-center" data-testid="incontext-feedback-thanks">
                  <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 ring-1 ring-emerald-100">
                    <Check className="h-6 w-6 text-emerald-600" />
                  </div>
                  <p className="font-serif-display text-lg text-stone-800">Thanks.</p>
                  <p className="text-[13px] text-stone-500 mt-1">Your feedback has been saved.</p>
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
                  <div className="flex gap-2" data-testid="incontext-feedback-tabs">
                    {typeBtn("problem", "Report a Problem", AlertTriangle, "incontext-tab-problem")}
                    {typeBtn("suggestion", "Suggestion or Comment", Lightbulb, "incontext-tab-suggestion")}
                  </div>

                  <div>
                    <label className="block text-[13px] text-stone-700 font-medium mb-1.5">
                      {type === "problem" ? "What seems wrong?" : "What would you like us to know?"}
                    </label>
                    <textarea
                      autoFocus
                      data-testid="incontext-feedback-comment"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      rows={5}
                      placeholder={type === "problem"
                        ? "e.g. I don't understand why The Writing Studio thinks this is passive voice."
                        : "e.g. It would help to see an example here."}
                      className="w-full bg-white border border-stone-300 rounded-sm p-3 text-[14px] leading-relaxed text-stone-900 outline-none focus:ring-1 focus:ring-[#8C3A2A] resize-y"
                    />
                  </div>

                  <div className="flex items-center gap-2 text-[12px] text-stone-400" data-testid="incontext-screenshot-status">
                    <Camera className="h-3.5 w-3.5" />
                    {shot ? "A screenshot of this screen will be attached." : "Feedback will be sent without a screenshot."}
                  </div>

                  <button
                    onClick={submit}
                    disabled={saving || !comment.trim()}
                    data-testid="incontext-feedback-submit"
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#8C3A2A] text-white text-[13.5px] py-2.5 rounded-sm hover:bg-[#6B2C20] transition-colors disabled:opacity-40"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    Send
                  </button>
                </div>
              )}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
